// Sets that could not reach the server, kept on the phone until they can.
//
// # WHY THIS EXISTS
//
// Gym wifi is where the logger failed. A set that could not be sent said "That
// set did not save" and the athlete was stuck on it until the network came
// back. Now the set is kept here, the logger carries on, and the set is sent
// later (useOfflineSync). Nothing in this file ever drops a set silently.
//
// # WHY IN THE APP, NOT THE SERVICE WORKER
//
// Background Sync is Chromium-only, and most athletes are on iPhones. Draining
// from the page is deterministic, testable in jsdom, and needs no dependency.
//
// # THE RULES THAT MATTER
//
// - The body is FROZEN at the tap: the exact request buildSetRequest made,
//   including clientId, sessionDate and weekNumber. Replay sends it unchanged.
//   A set logged at 23:50 and synced at 07:00 belongs to the earlier day, and the
//   clientId is what makes a replay of an already-saved set harmless (PERF-13).
// - Every item names the user who logged it, and a drain sends only the signed-in
//   user's. AthleteID comes from the token at replay time, so sending A's sets
//   with B's token would file them as B's training.
// - Only a 2xx removes an item.

const KEY = 'lts.offlineQueue.v1';

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Read the queue. Corrupt or missing storage is an empty queue, never a throw.
 * A logger that crashes because of a bad storage value is worse than one that
 * forgets a queue it could not read anyway.
 */
export function readAll(storage = defaultStorage()) {
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.id && item.user) : [];
  } catch {
    return [];
  }
}

function writeAll(items, storage = defaultStorage()) {
  if (!storage) return;
  try {
    storage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Storage full or blocked. Nothing better to do than keep running.
  }
}

/**
 * Keep a set for later.
 *
 * `path` is the API path relative to the API base, as the logger built it.
 * `body` is stored as given and is never rebuilt. The item's id IS the set's
 * clientId, so enqueuing the same set twice (a retap while offline) keeps one
 * copy.
 */
export function enqueue({ user, path, body, label }, { storage = defaultStorage(), now = Date.now } = {}) {
  if (!user || !path || !body?.clientId) {
    throw new Error('a queued set needs a user, a path and a body with a clientId');
  }
  const items = readAll(storage);
  if (items.some((item) => item.id === body.clientId)) return items;
  const next = [
    ...items,
    { id: body.clientId, user, path, body, label: label || '', queuedAt: now(), state: 'pending' },
  ];
  writeAll(next, storage);
  return next;
}

export function pendingFor(user, storage = defaultStorage()) {
  return readAll(storage).filter((item) => item.user === user && item.state === 'pending');
}

export function failedFor(user, storage = defaultStorage()) {
  return readAll(storage).filter((item) => item.user === user && item.state === 'failed');
}

/** Remove a "could not sync" item the athlete has read and dismissed. */
export function dismiss(id, storage = defaultStorage()) {
  writeAll(readAll(storage).filter((item) => !(item.id === id && item.state === 'failed')), storage);
}

/**
 * What an attempt's outcome means for the item.
 *
 *   done:   saved (201, or 200 for a replay of a set already saved). Remove it.
 *   retry:  the network, or the server, is not there. Keep it and try later.
 *   auth:   the session ended. Keep it until the athlete signs back in.
 *   failed: the server read the set and refused it. Retrying will never
 *           work. Keep it where the athlete can see why.
 */
export function classify(outcome) {
  const status = outcome?.status ?? outcome?.response?.status;
  if (status === undefined || status === null || status === 0) return 'retry';
  if (status >= 200 && status < 300) return 'done';
  if (status === 401) return 'auth';
  if (status === 408 || status === 429 || status >= 500) return 'retry';
  return 'failed';
}

function reasonOf(error) {
  const data = error?.response?.data;
  if (data?.details && data?.error) return `${data.error}: ${data.details}`;
  return data?.details || data?.error || `HTTP ${error?.response?.status}`;
}

/**
 * Send one user's pending sets, oldest first, and settle each.
 *
 * Stops at the first retry or auth outcome. If one set cannot get through right
 * now, the ones after it will not either, and sending them out of order would
 * reorder the athlete's history for no gain.
 *
 * `send(item)` performs the request and resolves with a response or rejects
 * with an axios-shaped error.
 */
export async function drain({ user, send, storage = defaultStorage() }) {
  const summary = { sent: 0, failed: 0, stoppedBy: null };
  for (const item of pendingFor(user, storage)) {
    let outcome;
    try {
      outcome = await send(item);
    } catch (error) {
      outcome = error;
    }
    const verdict = classify(outcome);

    if (verdict === 'done') {
      writeAll(readAll(storage).filter((i) => i.id !== item.id), storage);
      summary.sent += 1;
    } else if (verdict === 'failed') {
      writeAll(readAll(storage).map((i) => (i.id === item.id
        ? { ...i, state: 'failed', reason: reasonOf(outcome) }
        : i)), storage);
      summary.failed += 1;
    } else {
      summary.stoppedBy = verdict;
      break;
    }
  }
  return summary;
}
